import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  defaultCountryFieldName,
  FormProps,
  getAddressControlName,
  getRequiredValidationRule,
  getValidationErrorProps,
  isCanadaSelected,
  isUSSelected,
} from 'util/Form';
import { caStates, usStates } from 'store/configs/States';
import StaticSelectOption from 'store/types/StaticSelectOption';

interface StateFormItemProps extends FormProps {
  addressFieldName: string;
  countryFieldName?: string;
  generateControlName?: boolean;
}

const StateFormItem: React.FunctionComponent<StateFormItemProps> = ({
  fieldName = 'stateRegion',
  addressFieldName,
  countryFieldName = defaultCountryFieldName,
  required = true,
  disabled = false,
  generateControlName = true,
}) => {
  const {
    control,
    setValue,
    formState: { touchedFields },
  } = useFormContext();
  const touchedFieldValues = addressFieldName ? touchedFields[addressFieldName] : touchedFields;
  const stateControlName = useMemo(
    () => (generateControlName ? getAddressControlName(addressFieldName, fieldName) : fieldName),
    [addressFieldName, fieldName, generateControlName]
  );
  const countryControlName = useMemo(
    () => (generateControlName ? getAddressControlName(addressFieldName, countryFieldName) : countryFieldName),
    [addressFieldName, countryFieldName, generateControlName]
  );
  const countryValue = useWatch({ control, name: countryControlName });
  const stateValue = useWatch({ control, name: stateControlName });
  const label: string = useMemo(() => (isCanadaSelected(countryValue) ? 'Province' : 'State'), [countryValue]);
  const options: StaticSelectOption[] = useMemo(
    () => (isUSSelected(countryValue) ? usStates : isCanadaSelected(countryValue) ? caStates : []),
    [countryValue]
  );
  const fieldRequired: boolean = useMemo(
    () => required && (isUSSelected(countryValue) || isCanadaSelected(countryValue)),
    [countryValue, required]
  );
  const [selectedValue, setSelectedValue] = useState<StaticSelectOption | null>(null);

  useEffect(() => {
    if (
      touchedFieldValues &&
      touchedFieldValues[countryFieldName] &&
      options &&
      (isUSSelected(countryValue) || isCanadaSelected(countryValue))
    ) {
      setValue(stateControlName, '');
    }
  }, [touchedFieldValues, options, countryValue, stateControlName, countryFieldName, setValue]);

  useEffect(() => {
    setSelectedValue(options.find((state) => state.value === stateValue) || null);
  }, [options, stateValue]);

  return (
    <Controller
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          blurOnSelect
          autoSelect
          autoHighlight
          disableClearable={selectedValue !== null}
          options={options}
          getOptionLabel={(option) => option.text || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedValue}
          disabled={disabled}
          onChange={(_event, data) => {
            field.onChange(data?.value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...getValidationErrorProps(error)}
              required={fieldRequired}
              label={label}
              inputRef={field.ref}
              data-testid={`${fieldName}-text-field`}
              inputProps={{ ...params.inputProps, ['data-testid']: `${fieldName}-input` }}
              FormHelperTextProps={{ ['data-testid']: `${fieldName}-helper-text` } as any}
            />
          )}
        />
      )}
      name={stateControlName}
      control={control}
      rules={{
        required: getRequiredValidationRule(label.toLowerCase(), true, fieldRequired),
      }}
    />
  );
};
export default StateFormItem;
